<template>
  <info-panel
    v-if="flow"
    :item="flow"
    :title="title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{
      $t("labels.showEmptySections")
    }}</template>

    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template
      #header-image
      v-if="imageData && extraSettings.image?.value == 'icon'"
    >
      <img :src="imageData" />
    </template>
    <template #option-image-icon>{{ $t("labels.imageAsIcon") }}</template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template
      #description
      v-if="settingsValues.showEmptySections || !isEmpty(flow.description)"
    >
      <formatted-read-more-text
        :value="flow.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-value>
      {{ $t("labels.flowValue") }}
    </template>
    <template #label-_formula>
      {{ $t("words.formula") }}
    </template>
    <template #value>
      <div class="info-panel__table">
        <span class="label">{{ $t("words.currentValue") }}</span>
        <span>{{ formatNumber(calculatedValue?.value) }} {{ calculatedValue?.unit }}</span>

        <span class="label">{{ $t("words.startValue") }}</span>
        <span>{{ formatNumber(startValue?.value) }} {{ startValue?.unit }}</span>

        <span class="label">{{ $t("labels.valueRange") }}</span>
        <span>{{ formatNumber(startValue?.minValue) }} – {{ formatNumber(startValue?.maxValue) }}</span>

        <template v-if="sections._formula">
          <span class="label">{{ $t("labels.formula") }}:</span>
          <expression-rendered
            :expression="valueExpression"
            :parse-context="parseContext"
            class="info-panel__expression"
          />
        </template>
      </div>
    </template>

    <template #label-formulatree>{{ $t("labels.formulaTree") }}</template>
    <template #formulatree>
      <expression-tree-view
        class="expression-editor__tree-view"
        :expression="valueExpression"
        :parse-context="parseContext"
        :editable="false"
      />
    </template>

    <template #label-branches>
      {{ $t("objectNames.flowBranch", 4) }}
    </template>
    <template
      #branches
      v-if="settingsValues.showEmptySections || flowBranches.length > 0"
    >
      <div class="flow-infopanel__elementsrow">
        <div>
          <div class="label">
            {{ $t("words.source", sourceBranches.length) }}
          </div>
          <button-list
            :items="sourceBranches"
            v-slot="{ item }: { item: FlowBranchItem }"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <span v-if="items?.[item.elementType]?.[item.elementId]?.title">
              {{ items[item.elementType][item.elementId].title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-list>
        </div>

        <lucide-icon name="arrow-big-right" class="flow-infopanel__elementsrow-arrow" />

        <div>
          <div class="label">{{ $t("words.sink", sinkBranches.length) }}</div>
          <button-list
            :items="sinkBranches"
            v-slot="{ item }: { item: FlowBranchItem }"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <span v-if="items?.[item.elementType]?.[item.elementId]?.title">
              {{ items[item.elementType][item.elementId].title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-list>
        </div>
      </div>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { formatNumber } from "@/helpers/utility/Formatting";
import { useRoute } from "vue-router";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ExpressionRendered from "../expression/ExpressionRendered.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ExpressionTreeView from "../expression/ExpressionTreeView.vue";

import { useElements } from "../element/elements.ts";
import { useFlowStore } from "./flows.store";
import { useFlowBranchStore } from "./flowBranches.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useRandomValuesStore } from "@editor/items/expression/expressionRandomValues.ts";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";


import type { FlowItem } from "./FlowItem";
import type { FlowBranchItem } from "./FlowBranchItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { ExpressionParseContext } from "../effect/Expression.ts";

const props = defineProps<{
  flow: FlowItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const imageStore = useImageStore();
const flowStore = useFlowStore();
const route = useRoute();
const simulationViewStore = useSimulationViewStore();

const sections = reactive({
  description: true,
  value: true,
  _formula: true,
  formulatree: true,
  branches: true
});

const extraSettings = reactive({
  image: {
    type: "dropdown" as const,
    value: "side-full",
    options: ["icon", "side-full", "side-cut", "hidden"]
  },
  showEmptySections: { type: "check" as const, value: true }
});

const infoPanelStore = useInfoPanelStore();

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.flow = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.flow || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

loadSections();

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

const startValue = computed(() => {
  if (props.flow == null) return null;
  return useFlowStore().expressionValues[props.flow.uid] ?? null;
});

const calculatedValue = computed(() => {
  if (props.flow == null) return null;
  return simulationViewStore.elementValues[props.flow.uid];
});

const valueExpression = computed(() => {
  if (props.flow == null) return "";
  return useFlowStore().expressions[props.flow.id] ?? "";
});

const modelRandomValues = useRandomValuesStore();
const parseContext = computed((): ExpressionParseContext => {
  if (isSimulationOpen()) {
    const roundIndex = simulationViewStore.selectedRoundIndex;
    return {
      rounds: [{ elementValues: simulationViewStore.elementValues, index: roundIndex }],
      affectedType: 'flow',
      affectedId: props.flow?.id ?? -1,
      roundIndex: roundIndex,
      randomKey: props.flow?.uid ?? "",
      randomValues: simulationViewStore.selectedRound?.startRandomValues || {},
    };
  }

  return {
    rounds: [{
      elementValues: useElements().elementValues,
      index: 0
    }],
    affectedType: 'flow',
    affectedId: props.flow?.id ?? -1,
    roundIndex: 0,
    randomKey: props.flow?.uid ?? "",
    randomValues: modelRandomValues.randomValues
  }
});

const title = computed(() => {
  if (props.flow == null) return "";
  return flowStore.titles[props.flow?.id] ?? "";
});

function isSimulationOpen() {
  if (!["simulation", "inspector"].includes(route.meta.areaName as string))
    return false;
  if (route.meta.areaName === "inspector") {
    if (useInspectorView().subview != "matrix") return false;
  }
  return simulationViewStore.simulation != null;
}

// BRANCHES

const flowBranches = computed(() => {
  if (props.flow == null) return [];
  return useFlowBranchStore().byFlow[props.flow.id] ?? [];
});

const sourceBranches = computed(() => {
  return flowBranches.value.filter((b) => b.branchType === "source");
});
const sinkBranches = computed(() => {
  return flowBranches.value.filter((b) => b.branchType === "sink");
});

const image = computed(() => {
  if (props.flow && props.flow.imageId != null) {
    return imageStore.items[props.flow.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.flow = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.flow || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style lang="scss">
.flow-infopanel {
  &__elementsrow {
    display: grid;
    grid-template-columns: minmax(5rem, 20rem) 2rem minmax(5rem, 20rem);
    gap: 0.5rem;
    align-items: flex-start;

    &-arrow {
      justify-self: center;
      margin-top: 1.9rem;
    }
  }
}
</style>
